"use client"

import { useEffect, useState } from "react"
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  writeBatch,
  getDoc,
  deleteDoc,
} from "firebase/firestore"
import { db } from "../firebase"
import { useAuth } from "../context/AuthContext"
import Header from "./header"
import { Bell, Check, X } from "lucide-react"

function Notifications({ isOverlay = false, onClose }) {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const { currentUser, userProfile } = useAuth()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768) // 768px is typical md breakpoint
    }

    handleResize() // Initial check
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true)
        const notificationsRef = collection(db, "Notifications")
        const q = query(notificationsRef, where("toUid", "==", currentUser.uid))
        const querySnapshot = await getDocs(q)
        const fetchedNotifications = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        setNotifications(fetchedNotifications)
        
        // Mark all notifications as read when user opens the notifications screen
        await markAllAsRead(fetchedNotifications)
      } catch (error) {
        console.error("Error fetching notifications:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchNotifications()
  }, [currentUser.uid])

  const markAllAsRead = async (notificationsToMark) => {
    try {
      const batch = writeBatch(db)
      notificationsToMark.forEach((notification) => {
        if (!notification.read) {
          const notificationRef = doc(db, "Notifications", notification.id)
          batch.update(notificationRef, { read: true })
        }
      })
      await batch.commit()
      
      // Update local state
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({ ...notification, read: true }))
      )
    } catch (error) {
      console.error("Error marking notifications as read:", error)
    }
  }

  const handleConfirm = async (notificationId, fromUid, toUid) => {
    try {
      // Fetch the "from" user's email using their UID
      const fromUserRef = doc(db, "users", fromUid)
      const fromUserSnapshot = await getDoc(fromUserRef)

      if (!fromUserSnapshot.exists()) {
        console.error("User not found.")
        return
      }

      const fromUserData = fromUserSnapshot.data()
      const fromEmail = fromUserData.email // Retrieve the email from the user's data

      // Update the notification status to Confirmed
      await updateDoc(doc(db, "Notifications", notificationId), {
        status: "Confirmed",
      })

      // Add the users as friends to each other
      const currentUserRef = doc(db, "users", toUid)

      console.log("Adding friendship...")

      // Add the "from" user to the current user's friends list
      await updateDoc(currentUserRef, {
        friends: arrayUnion(fromUserData.email), // Use the current user's email
      })

      // Add the current user to the "from" user's friends list
      await updateDoc(fromUserRef, {
        friends: arrayUnion(currentUser.email),
      })

      // Remove the notification from the list
      setNotifications(notifications.filter((notification) => notification.id !== notificationId))
      await deleteDoc(doc(db, "Notifications", notificationId))

      alert("Friend Request Accepted!")
    } catch (error) {
      console.error("Error confirming friend request:", error)
    }
  }

  const handleDeny = async (notificationId) => {
    try {
      // Update the notification status to Denied
      await updateDoc(doc(db, "Notifications", notificationId), {
        status: "Denied",
      })

      // Remove the notification from the list
      setNotifications(notifications.filter((notification) => notification.id !== notificationId))
      await deleteDoc(doc(db, "Notifications", notificationId))
    } catch (error) {
      console.error("Error denying friend request:", error)
    }
  }

  const handleClearAll = async () => {
    try {
      const batch = writeBatch(db)
      notifications.forEach((notification) => {
        batch.delete(doc(db, "Notifications", notification.id))
      })
      await batch.commit()
      setNotifications([])
    } catch (error) {
      console.error("Error clearing all notifications:", error)
    }
  }

  const NotificationContent = () => (
    <div className="flex flex-col h-full bg-gradient-to-b from-gray-900 to-black">
      <div className="flex justify-between items-center mb-6 px-6 pt-6">
        <div className="flex items-center">
          <Bell className="w-6 h-6 text-emerald-400 mr-3" />
          <h2 className="text-white text-2xl font-bold">Notifications</h2>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white bg-gray-800/70 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-gray-800/50 backdrop-blur-sm border p-4 mb-4 rounded-xl flex items-start relative hover:border-emerald-500/30 transition-all duration-300 ${
                !notification.read ? 'border-emerald-500/50' : 'border-gray-700'
              }`}
            >
              <img
                src={
                  notification.profilePicUrl ||
                  "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
                }
                alt={notification.fromUsername}
                className="w-12 h-12 rounded-full mr-4 border border-gray-700"
              />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-white">{notification.fromUsername}</h2>
                <p className="text-gray-300">{notification.content}</p>
                {notification.type === "Request" && (
                  <div className="flex justify-end mt-3 space-x-2">
                    <button
                      className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-1.5 rounded-full text-sm flex items-center transition-colors"
                      onClick={() => handleConfirm(notification.id, notification.fromUid, currentUser.uid)}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Accept
                    </button>
                    <button
                      className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-1.5 rounded-full text-sm flex items-center transition-colors"
                      onClick={() => handleDeny(notification.id)}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Decline
                    </button>
                  </div>
                )}
              </div>
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-white bg-gray-700/50 rounded-full w-6 h-6 flex items-center justify-center transition-colors"
                onClick={() => handleDeny(notification.id)}
              >
                ✕
              </button>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="bg-gray-800/70 p-4 rounded-xl inline-block mb-4">
              <Bell className="w-12 h-12 text-gray-500 mx-auto" />
            </div>
            <h2 className="text-xl font-medium mb-2 text-white">No Notifications</h2>
            <p className="text-gray-400">You're all caught up!</p>
          </div>
        )}

        {notifications.length > 0 && (
          <button
            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-3 rounded-lg mb-6 w-full transition-colors border border-gray-700"
            onClick={handleClearAll}
          >
            Clear All
          </button>
        )}
      </div>
    </div>
  )

  if (isOverlay) {
    // Desktop overlay: left sliding panel.
    if (!isMobile) {
      return (
        <div className="fixed inset-y-0 left-0 w-[30%] min-w-[30%] bg-gradient-to-b from-gray-900 to-black shadow-lg transform transition-transform duration-300 ease-in-out z-50 border-r border-gray-800">
          <NotificationContent />
        </div>
      )
    }

    // Mobile overlay: full-height panel at 90% width (not centered modal).
    return (
      <div className="fixed inset-y-0 left-0 z-50 w-[90vw] max-w-[90vw] overflow-hidden border-r border-gray-800 bg-gradient-to-b from-gray-900 to-black shadow-xl">
        <NotificationContent />
      </div>
    )
  }

  // For mobile or non-overlay view, render the full page
  return (
    <div>
      {!isOverlay && isMobile && <Header onToggle={null} currentView="notifications" />}
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black">
        <NotificationContent />
      </div>
    </div>
  )
}

export default Notifications

